import React, {useState, useRef, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {useClickOutside} from '../../../hooks/useClickOutside.jsx';
import css from './AsyncSearchableDropdown.module.scss';

const AsyncSearchableDropdown = ({
  label,
  value,
  onChange,
  loadOptions,
  fixedOptions = [],
  defaultOption = null,
  placeholder = 'Search and select an option',
  required = false,
  disabled = false,
  error = '',
  name,
  minChars = 0,
  debounceMs = 300,
  noOptionsText = 'No options found',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState(defaultOption);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const loadOptionsRef = useRef(loadOptions);
  const requestIdRef = useRef(0);

  useEffect(() => {
    loadOptionsRef.current = loadOptions;
  }, [loadOptions]);

  useClickOutside(dropdownRef, () => setIsOpen(false));

  useEffect(() => {
    const option = [defaultOption, ...fixedOptions].find(opt => opt && opt.value === value);
    setSelectedOption(option || null);
  }, [value, defaultOption, fixedOptions]);

  const fetchOptions = useCallback(async (query) => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      const result = await loadOptionsRef.current(query);
      if (requestId === requestIdRef.current) {
        setOptions(Array.isArray(result) ? result : []);
      }
    } catch (e) {
      if (requestId === requestIdRef.current) {
        setOptions([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (searchTerm.length < minChars) {
      setOptions([]);
      setIsLoading(false);

      return;
    }

    const handle = setTimeout(() => {
      fetchOptions(searchTerm);
    }, debounceMs);

    return () => clearTimeout(handle);
  }, [searchTerm, isOpen, minChars, debounceMs, fetchOptions]);

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
    setIsOpen(true);
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setSearchTerm('');
    setIsOpen(false);
    onChange({
      target: {
        name,
        value: option.value,
      },
    }, option);
  };

  const visibleOptions = [...fixedOptions, ...options];
  const showHint = searchTerm.length < minChars;

  return (
    <div className={css.dropdownContainer} ref={dropdownRef}>
      {label && (
        <label className={css.label}>
          {label}
          {required && <span className={css.required}>*</span>}
        </label>
      )}
      <div className={`${css.dropdown} ${error ? css.error : ''} ${disabled ? css.disabled : ''}`}>
        <input
          type="text"
          className={css.input}
          value={isOpen ? searchTerm : (selectedOption ? selectedOption.label : '')}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={selectedOption ? selectedOption.label : placeholder}
          disabled={disabled}
          required={required}
          name={name}
        />
        {isOpen && !disabled && (
          <div className={css.optionsContainer}>
            {fixedOptions.map((option) => (
              <div key={`fixed-${option.value}`}
                   className={`${css.option} ${selectedOption?.value === option.value ? css.selected : ''}`}
                   onClick={() => handleOptionClick(option)}
              >
                {option.label}
              </div>
            ))}
            {isLoading ? (
              <div className={css.noOptions}>Loading…</div>
            ) : showHint ? (
              <div className={css.noOptions}>Type at least {minChars} characters to search</div>
            ) : options.length > 0 ? (
              options.map((option) => (
                <div key={option.value}
                     className={`${css.option} ${selectedOption?.value === option.value ? css.selected : ''}`}
                     onClick={() => handleOptionClick(option)}
                >
                  {option.label}
                </div>
              ))
            ) : visibleOptions.length === 0 ? (
              <div className={css.noOptions}>{noOptionsText}</div>
            ) : null}
          </div>
        )}
      </div>
      {error && <span className={css.errorMessage}>{error}</span>}
    </div>
  );
};

AsyncSearchableDropdown.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  loadOptions: PropTypes.func.isRequired,
  fixedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  defaultOption: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  }),
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  name: PropTypes.string.isRequired,
  minChars: PropTypes.number,
  debounceMs: PropTypes.number,
  noOptionsText: PropTypes.string,
};

export default AsyncSearchableDropdown;
